// Copyright (c) Microsoft. All rights reserved.

import { Middleware } from '@reduxjs/toolkit';
import { IChatMessage } from './../../../libs/models/ChatMessage';
import { RootState, getSelectedChatID } from './../../app/store';
import { getOrCreateHubConnection } from './signalRHubConnection';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';

// The action sent to the SignalR middleware.
interface SignalRAction {
    type: string;
    payload: {
        message?: IChatMessage;
        userId?: string;
        id?: string;
    };
}

const stripLocalImagePreview = (message: IChatMessage | undefined): IChatMessage | undefined => {
    if (!message?.localImagePreview) return message;
    const { localImagePreview: _omit, ...rest } = message;
    return rest;
};

export const signalRMiddleware: Middleware<object, RootState> = (store) => {
    const SIGNALR_ACTIONS = [
        'conversations/addMessageToConversationFromUser',
        'conversations/setConversations',
        'conversations/addConversation',
    ] as const;

    return (next) => async (action) => {
        const result = next(action);

        // RTK v2 / Redux v5 types `action` as `unknown`. Guard that it's a real
        // action object before casting, so a non-object dispatch (null/primitive)
        // can't blow up on `.type` access. The cast is only exercised past this point.
        if (typeof action !== 'object' || action === null || !('type' in action)) {
            return result;
        }

        const signalRAction = action as SignalRAction;

        if (!SIGNALR_ACTIONS.includes(signalRAction.type as (typeof SIGNALR_ACTIONS)[number])) {
            return result;
        }

        try {
            const hubConnection: HubConnection | null = await getOrCreateHubConnection(store);

            if (hubConnection?.state !== HubConnectionState.Connected) {
                console.log('SignalR connection not established, skipping action', signalRAction.type);
                return result;
            }

            switch (signalRAction.type) {
                case 'conversations/addMessageToConversationFromUser':
                    try {
                        // Strip localImagePreview before relay — it's a multi-MB base64 data
                        // URL that's only meaningful in the local Redux store ("never
                        // persisted; gone after a page refresh"). Sending it over the hub
                        // overshoots SignalR's default MaximumReceiveMessageSize of 32 KB
                        // and forces the server to close the WebSocket on every image
                        // submit. Other clients in the group don't need it.
                        const messageForRelay = stripLocalImagePreview(signalRAction.payload.message);
                        await hubConnection.invoke(
                            'SendMessageAsync',
                            getSelectedChatID(),
                            store.getState().app.activeUserInfo?.id,
                            messageForRelay,
                        );
                    } catch (err: unknown) {
                        // Connection-state alerts are owned by signalRHubConnection.ts
                        // (onclose / onreconnecting / onreconnected); chat HTTP errors are
                        // surfaced by useChat.getResponse. Log only — don't double-alert
                        // with the raw library error string.
                        console.error('Error sending message:', err);
                    }
                    break;

                case 'conversations/setConversations':
                    try {
                        await Promise.all(
                            Object.keys(signalRAction.payload).map(async (id) => {
                                await hubConnection.invoke('AddClientToGroupAsync', id);
                            }),
                        );
                    } catch (err: unknown) {
                        console.error('Error setting conversations:', err);
                    }
                    break;

                case 'conversations/addConversation':
                    try {
                        await hubConnection.invoke('AddClientToGroupAsync', signalRAction.payload.id);
                    } catch (err: unknown) {
                        console.error('Error adding conversation:', err);
                    }
                    break;
            }
        } catch (error) {
            console.error('Error in signalR middleware:', error);
        }

        return result;
    };
};
